import React from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import LanguageIcon from "@material-ui/icons/Language";
import EventIcon from "@material-ui/icons/Event";

import { i18n } from "../../translate/i18n";
import { formatAge } from "./format";

const WEB_LEADS_TAB = 3; // pestaña «Solicitudes web» de la Agenda (pages/Appointments)

const useStyles = makeStyles((theme) => ({
  grid: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", alignItems: "start" },
  card: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  count: {
    marginLeft: "auto",
    minWidth: 24,
    height: 24,
    padding: "0 8px",
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "12px 16px",
    border: "none",
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    color: theme.palette.text.primary,
    "&:hover": { backgroundColor: theme.palette.action.hover },
    "&:last-child": { borderBottom: "none" },
  },
  main: { flexGrow: 1, minWidth: 0 },
  title: { fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  sub: { fontSize: 12.5, color: theme.palette.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  age: { fontSize: 12, color: theme.palette.text.secondary, flexShrink: 0 },
  empty: { padding: "28px 16px", textAlign: "center", fontSize: 14, color: theme.palette.text.secondary },
}));

// Cita: `at` es hora de pared «AAAA-MM-DD HH:mm:ss» (no se convierte, doc 0412 §11).
const wallClock = (at) => (at ? `${at.slice(8, 10)}/${at.slice(5, 7)} ${at.slice(11, 16)}` : "");

/** Pestaña «Pendientes» (doc 0447 §5): lo accionable ahora, cada fila lleva a su pantalla. */
const PendingTab = ({ items }) => {
  const classes = useStyles();
  const history = useHistory();
  const chats = items.filter((i) => i.kind === "chat");
  const leads = items.filter((i) => i.kind === "lead");
  const appointments = items.filter((i) => i.kind === "appointment");

  const section = (Icon, title, list, render, onOpen) => (
    <section className={classes.card} aria-label={title}>
      <div className={classes.head}>
        <Icon fontSize="small" />
        {title}
        <span className={classes.count}>{list.length}</span>
      </div>
      {list.length === 0 ? (
        <div className={classes.empty}>{i18n.t("reports.pending.none")}</div>
      ) : (
        list.map((it) => (
          <button key={`${it.kind}-${it.id}`} type="button" className={classes.row} onClick={() => onOpen(it)}>
            {render(it)}
          </button>
        ))
      )}
    </section>
  );

  return (
    <div className={classes.grid}>
      {section(
        ChatBubbleOutlineIcon,
        i18n.t("reports.pending.chats"),
        chats,
        (it) => (
          <>
            <span className={classes.main}>
              <div className={classes.title}>{it.title}</div>
              <div className={classes.sub}>{it.subtitle || i18n.t("reports.noQueue")}</div>
            </span>
            <span className={classes.age}>{formatAge(it.ageMinutes)}</span>
          </>
        ),
        (it) => history.push(`/tickets/${it.ref}`)
      )}
      {section(
        LanguageIcon,
        i18n.t("reports.pending.leads"),
        leads,
        (it) => (
          <>
            <span className={classes.main}>
              <div className={classes.title}>{it.title}</div>
              <div className={classes.sub}>{it.subtitle || "—"}</div>
            </span>
            <span className={classes.age}>{formatAge(it.ageMinutes)}</span>
          </>
        ),
        () => history.push("/schedules", { tabIndex: WEB_LEADS_TAB })
      )}
      {section(
        EventIcon,
        i18n.t("reports.pending.appointments"),
        appointments,
        (it) => (
          <>
            <span className={classes.main}>
              <div className={classes.title}>{it.title}</div>
              <div className={classes.sub}>{i18n.t(`reports.pending.status.${it.subtitle}`, { defaultValue: it.subtitle })}</div>
            </span>
            <span className={classes.age}>{wallClock(it.at)}</span>
          </>
        ),
        () => history.push("/schedules")
      )}
    </div>
  );
};

export default PendingTab;
